// Centralized operative activities store

export interface OperativeActivity {
  id: string
  activityType: 'standard' | 'personalizzata'
  contractNumber: string
  clientName: string
  operator: string
  deadline: string // ISO date string
  title: string
  product: string
  stepsCompleted: boolean[]
  createdAt: string
  numActivitiesContracted?: number
  // For custom activities, the names of each step
  customStepNames?: string[]
}

// Standard step names (predefined flow)
export const standardStepTitles = [
  'Calendarizzazione',
  'Sopralluogo',
  'Attività di back office',
  'Consegna',
  'Attività integrativa',
]

// Keep backward compat alias
export const stepTitles = standardStepTitles

// Custom step type options (for personalizzata activities)
export const customStepOptions = [
  'Raccolta informazioni e documenti',
  'Redazione documentazione',
  'Aggiornamento del fascicolo cliente',
  'Piano correttivo e di miglioramento',
  'Calendarizzazione appuntamento',
  'Sopralluogo fisico',
  'Sopralluogo online',
  'Consegna fisica',
  'Consegna online',
  'Consulenza',
  'Analisi e ricerca',
  'Riunione',
  'Altro',
]

export function getStepTitlesForActivity(
  activity: OperativeActivity,
): string[] {
  if (activity.activityType === 'personalizzata' && activity.customStepNames) {
    return activity.customStepNames
  }
  return standardStepTitles
}

export function getCurrentStepName(activity: OperativeActivity): string {
  const titles = getStepTitlesForActivity(activity)
  const allDone = activity.stepsCompleted.every(Boolean)
  if (allDone) return 'Conclusa'

  for (let i = 0; i < activity.stepsCompleted.length; i++) {
    if (!activity.stepsCompleted[i]) {
      return titles[i] || 'Sconosciuto'
    }
  }
  return 'Conclusa'
}

export function getProgress(activity: OperativeActivity) {
  const totalSteps = activity.stepsCompleted.length
  const completedCount = activity.stepsCompleted.filter(Boolean).length
  const isCompleted = completedCount === totalSteps
  const now = new Date()
  const deadlineDate = new Date(activity.deadline)
  const isOverdue = !isCompleted && deadlineDate < now

  let percentage: number

  if (activity.activityType === 'standard') {
    // Standard: specific percentages per step
    // Calendarizzazione=20%, Sopralluogo=40%, Back office=60%, Consegna=80%, Attività integrativa=90%, Conclusa=100%
    if (isCompleted) {
      percentage = 100
    } else {
      const standardPercentages = [20, 40, 60, 80, 90]
      // Find current step (first uncompleted)
      let currentIdx = totalSteps - 1
      for (let i = 0; i < totalSteps; i++) {
        if (!activity.stepsCompleted[i]) {
          currentIdx = i
          break
        }
      }
      // Percentage = previous step's percentage, or 0 if first step
      percentage = currentIdx > 0 ? standardPercentages[currentIdx - 1] : 0
    }
  } else {
    // Custom: percentage based on completed/total
    percentage =
      totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0
  }

  // currentStep = first uncompleted step (1-indexed), or totalSteps if all done
  let currentStep = totalSteps
  for (let i = 0; i < totalSteps; i++) {
    if (!activity.stepsCompleted[i]) {
      currentStep = i + 1
      break
    }
  }

  return {
    currentStep,
    percentage,
    isCompleted,
    isOverdue,
    completedCount,
    totalSteps,
  }
}

const initialActivities: OperativeActivity[] = [
  {
    id: 'oa-001',
    activityType: 'standard',
    contractNumber: 'C-2024/001',
    clientName: 'Metalmeccanica Rossi SRL',
    operator: 'Alessio',
    deadline: '2025-12-15',
    title: 'Consulenza Sicurezza Annuale',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, true, true, true, false], // 80% → Consegna done, at Attività integrativa
    createdAt: '2025-01-10',
  },
  {
    id: 'oa-002',
    activityType: 'standard',
    contractNumber: 'C-2024/002',
    clientName: 'Edilizia Moderna SPA',
    operator: 'Alessio',
    deadline: '2025-11-30',
    title: 'HACCP Base',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, false, false], // 60%
    createdAt: '2025-02-05',
  },
  {
    id: 'oa-003',
    activityType: 'personalizzata',
    contractNumber: 'C-2024/003',
    clientName: 'Supermercati Market',
    operator: 'Alessio',
    deadline: '2025-10-20',
    title: 'Privacy GDPR',
    product: 'Privacy GDPR',
    stepsCompleted: [true, true, false, false],
    customStepNames: [
      'Raccolta informazioni e documenti',
      'Redazione documentazione',
      'Consulenza',
      'Consegna online',
    ],
    createdAt: '2025-03-01',
  },
  {
    id: 'oa-004',
    activityType: 'standard',
    contractNumber: 'C-2024/004',
    clientName: 'Ristorante Da Mario',
    operator: 'Alessio',
    deadline: '2025-09-28',
    title: 'DVR Aggiornamento',
    product: 'DVR',
    stepsCompleted: [true, false, false, false, false], // 20%
    createdAt: '2024-11-15',
  },
  {
    id: 'oa-005',
    activityType: 'standard',
    contractNumber: 'C-2024/005',
    clientName: 'Studio Legale Bianchi',
    operator: 'Alessio',
    deadline: '2025-08-01',
    title: 'Formazione Lavoratori',
    product: 'Formazione Lavoratori',
    stepsCompleted: [true, true, true, true, true], // 100%
    createdAt: '2024-12-01',
  },
  {
    id: 'oa-006',
    activityType: 'personalizzata',
    contractNumber: 'C-2024/006',
    clientName: 'Autofficina Veloce',
    operator: 'Alessio',
    deadline: '2026-06-30',
    title: 'Consulenza Sicurezza',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [false, false, false],
    customStepNames: [
      'Sopralluogo fisico',
      'Redazione documentazione',
      'Consegna fisica',
    ],
    createdAt: '2025-05-01',
  },
  {
    id: 'oa-007',
    activityType: 'standard',
    contractNumber: 'C-2024/007',
    clientName: 'Bar Centrale',
    operator: 'Alessio',
    deadline: '2025-07-15',
    title: 'HACCP Rinnovo',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, false, false], // 60%
    createdAt: '2025-05-10',
  },
  {
    id: 'oa-008',
    activityType: 'standard',
    contractNumber: 'C-2024/008',
    clientName: 'Farmacia Salute',
    operator: 'Alessio',
    deadline: '2025-09-01',
    title: 'Sicurezza Annuale',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, false, false, false, false], // 20%
    createdAt: '2025-01-05',
  },
  {
    id: 'oa-009',
    activityType: 'personalizzata',
    contractNumber: 'C-2024/009',
    clientName: 'Hotel Bellavista',
    operator: 'Lorenzo',
    deadline: '2025-12-31',
    title: 'Privacy GDPR',
    product: 'Privacy GDPR',
    stepsCompleted: [true, true, false, false, false],
    customStepNames: [
      'Raccolta informazioni e documenti',
      'Analisi e ricerca',
      'Redazione documentazione',
      'Consulenza',
      'Consegna online',
    ],
    createdAt: '2025-02-20',
  },
  {
    id: 'oa-010',
    activityType: 'standard',
    contractNumber: 'C-2024/010',
    clientName: 'Panificio Dolce',
    operator: 'Gian Franco',
    deadline: '2026-03-15',
    title: 'HACCP Base',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, true, false], // 80%
    createdAt: '2025-04-01',
  },
  {
    id: 'oa-011',
    activityType: 'standard',
    contractNumber: 'C-2025/011',
    clientName: 'Ceramiche Toscane SRL',
    operator: 'Alessio',
    deadline: '2026-04-15',
    title: 'Ambiente Audit Periodico',
    product: 'Consulenza Ambiente',
    stepsCompleted: [true, true, false, false, false],
    createdAt: '2025-11-10',
  },
  {
    id: 'oa-012',
    activityType: 'personalizzata',
    contractNumber: 'C-2025/012',
    clientName: 'Gioielleria Preziosi',
    operator: 'Alessio',
    deadline: '2026-05-20',
    title: 'Antiriciclaggio Verifica',
    product: 'Antiriciclaggio',
    stepsCompleted: [true, false, false],
    customStepNames: [
      'Raccolta informazioni e documenti',
      'Aggiornamento del fascicolo cliente',
      'Consulenza',
    ],
    createdAt: '2025-12-01',
  },
  {
    id: 'oa-013',
    activityType: 'standard',
    contractNumber: 'C-2025/013',
    clientName: 'Impresa Edile Costruzioni',
    operator: 'Alessio',
    deadline: '2026-06-10',
    title: 'Sicurezza Cantiere Nuovo',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, true, true, false, false],
    createdAt: '2025-10-15',
  },
  {
    id: 'oa-014',
    activityType: 'standard',
    contractNumber: 'C-2025/014',
    clientName: 'Trattoria Il Poggio',
    operator: 'Alessio',
    deadline: '2026-07-01',
    title: 'HACCP Rinnovo Annuale',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, true, false],
    createdAt: '2025-09-20',
  },
  {
    id: 'oa-015',
    activityType: 'standard',
    contractNumber: 'C-2025/015',
    clientName: 'Ottica Visione Chiara',
    operator: 'Alessio',
    deadline: '2026-08-15',
    title: 'Privacy Assessment Annuale',
    product: 'Privacy GDPR',
    stepsCompleted: [false, false, false, false, false],
    createdAt: '2026-01-10',
  },
  {
    id: 'oa-016',
    activityType: 'standard',
    contractNumber: 'C-2025/016',
    clientName: 'Mobilificio Artigiano',
    operator: 'Alessio',
    deadline: '2026-09-30',
    title: 'DVR Nuovo Impianto',
    product: 'DVR',
    stepsCompleted: [false, false, false, false, false],
    createdAt: '2026-02-01',
  },
  {
    id: 'oa-017',
    activityType: 'standard',
    contractNumber: 'C-2025/017',
    clientName: 'Palestra FitLife',
    operator: 'Alessio',
    deadline: '2026-03-31',
    title: 'Formazione Preposti',
    product: 'Formazione Lavoratori',
    stepsCompleted: [true, true, false, false, false],
    createdAt: '2025-11-25',
  },
  {
    id: 'oa-c001',
    activityType: 'standard',
    contractNumber: '25.004',
    clientName: 'Impresa 3 SPA',
    operator: 'Alessio',
    deadline: '2026-05-30',
    title: 'Formazione Lavoratori',
    product: 'Formazione Lavoratori',
    stepsCompleted: [true, true, true, false, false],
    createdAt: '2025-04-10',
  },
  {
    id: 'oa-c002',
    activityType: 'standard',
    contractNumber: '25.004',
    clientName: 'Impresa 3 SPA',
    operator: 'Lorenzo',
    deadline: '2026-07-15',
    title: 'DVR Aggiornamento',
    product: 'DVR',
    stepsCompleted: [true, false, false, false, false],
    createdAt: '2025-05-01',
  },
  {
    id: 'oa-c003',
    activityType: 'standard',
    contractNumber: '25.010',
    clientName: 'Azienda 9 SRL',
    operator: 'Alessio',
    deadline: '2026-04-20',
    title: 'Consulenza Sicurezza Periodica',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, true, true, true, false],
    createdAt: '2025-03-15',
  },
  {
    id: 'oa-c004',
    activityType: 'personalizzata',
    contractNumber: '25.010',
    clientName: 'Azienda 9 SRL',
    operator: 'Gian Franco',
    deadline: '2026-06-01',
    title: 'Sopralluogo Annuale',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, true, false, false],
    customStepNames: [
      'Calendarizzazione appuntamento',
      'Sopralluogo fisico',
      'Redazione documentazione',
      'Consegna fisica',
    ],
    createdAt: '2025-06-10',
  },
  {
    id: 'oa-c005',
    activityType: 'standard',
    contractNumber: '25.016',
    clientName: 'Azienda 15 SRL',
    operator: 'Lorenzo',
    deadline: '2026-08-10',
    title: 'Privacy GDPR Audit',
    product: 'Privacy GDPR',
    stepsCompleted: [true, true, true, true, true],
    createdAt: '2025-02-20',
  },
  {
    id: 'oa-c006',
    activityType: 'personalizzata',
    contractNumber: '25.016',
    clientName: 'Azienda 15 SRL',
    operator: 'Alessio',
    deadline: '2026-09-15',
    title: 'Formazione Privacy',
    product: 'Formazione Lavoratori',
    stepsCompleted: [false, false],
    customStepNames: ['Riunione', 'Consulenza'],
    createdAt: '2026-01-05',
  },
  {
    id: 'oa-c007',
    activityType: 'standard',
    contractNumber: '25.022',
    clientName: 'Azienda 21 SRL',
    operator: 'Alessio',
    deadline: '2026-06-20',
    title: 'HACCP Verifica Periodica',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, false, false],
    createdAt: '2025-07-01',
  },
  {
    id: 'oa-c008',
    activityType: 'standard',
    contractNumber: '25.028',
    clientName: 'Studio 28 Associati',
    operator: 'Gian Franco',
    deadline: '2026-05-10',
    title: 'Consulenza Ambiente',
    product: 'Consulenza Ambiente',
    stepsCompleted: [true, true, true, true, false],
    createdAt: '2025-08-15',
  },
  {
    id: 'oa-c009',
    activityType: 'standard',
    contractNumber: '25.034',
    clientName: 'Studio 34 Associati',
    operator: 'Lorenzo',
    deadline: '2026-04-30',
    title: 'Antiriciclaggio Check',
    product: 'Antiriciclaggio',
    stepsCompleted: [true, false, false, false, false],
    createdAt: '2025-09-01',
  },
  // Activities for "Azienda 0 SRL" (default client detail page, id=1)
  {
    id: 'oa-a0-001',
    activityType: 'standard',
    contractNumber: 'C-2025/101',
    clientName: 'Azienda 0 SRL',
    operator: 'Alessio',
    deadline: '2026-05-15',
    title: 'Consulenza Sicurezza Annuale',
    product: 'Consulenza Sicurezza',
    stepsCompleted: [true, true, true, false, false],
    createdAt: '2025-10-01',
  },
  {
    id: 'oa-a0-002',
    activityType: 'personalizzata',
    contractNumber: 'C-2025/102',
    clientName: 'Azienda 0 SRL',
    operator: 'Lorenzo',
    deadline: '2026-07-30',
    title: 'Privacy GDPR Assessment',
    product: 'Privacy GDPR',
    stepsCompleted: [true, false, false, false],
    customStepNames: [
      'Raccolta informazioni e documenti',
      'Analisi e ricerca',
      'Redazione documentazione',
      'Consegna online',
    ],
    createdAt: '2025-11-15',
  },
  {
    id: 'oa-a0-003',
    activityType: 'standard',
    contractNumber: 'C-2025/103',
    clientName: 'Azienda 0 SRL',
    operator: 'Alessio',
    deadline: '2025-12-20',
    title: 'HACCP Rinnovo',
    product: 'HACCP Base',
    stepsCompleted: [true, true, true, true, true],
    createdAt: '2025-06-01',
  },
]

class OperativeActivitiesStore {
  private activities: OperativeActivity[] = [...initialActivities]

  getAll(): OperativeActivity[] {
    return [...this.activities]
  }

  getByOperator(name: string): OperativeActivity[] {
    return this.activities.filter((a) => a.operator === name)
  }

  getByContract(contractNumber: string): OperativeActivity[] {
    return this.activities.filter((a) => a.contractNumber === contractNumber)
  }

  getByClient(clientName: string): OperativeActivity[] {
    return this.activities.filter((a) => a.clientName === clientName)
  }

  addActivity(
    activity: Omit<OperativeActivity, 'id' | 'createdAt' | 'stepsCompleted'>,
  ): OperativeActivity {
    const numSteps =
      activity.activityType === 'personalizzata' && activity.customStepNames
        ? activity.customStepNames.length
        : 5
    const newActivity: OperativeActivity = {
      ...activity,
      id: 'oa-' + Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString().split('T')[0],
      stepsCompleted: new Array(numSteps).fill(false),
    }
    this.activities.push(newActivity)
    return newActivity
  }

  completeStep(activityId: string, stepIndex: number): boolean {
    const activity = this.activities.find((a) => a.id === activityId)
    if (!activity) return false
    // Enforce sequential: all previous steps must be completed
    for (let i = 0; i < stepIndex; i++) {
      if (!activity.stepsCompleted[i]) return false
    }
    activity.stepsCompleted[stepIndex] = true
    return true
  }

  getById(id: string): OperativeActivity | undefined {
    return this.activities.find((a) => a.id === id)
  }

  updateActivity(
    id: string,
    updates: Partial<OperativeActivity>,
  ): OperativeActivity | undefined {
    const index = this.activities.findIndex((a) => a.id === id)
    if (index === -1) return undefined

    this.activities[index] = {
      ...this.activities[index],
      ...updates,
    }
    return this.activities[index]
  }
}

export const operativeActivitiesStore = new OperativeActivitiesStore()
